import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArticlePicker } from "@/components/ArticlePicker";
import { api, apiError } from "@/lib/api";

export function KitDialog({ open, onOpenChange, kit, onSaved }) {
  const editing = !!kit;
  const [code, setCode] = useState("");
  const [designation, setDesignation] = useState("");
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCode(kit?.code || "");
    setDesignation(kit?.designation || "");
    setRows(kit ? kit.components.map((c) => ({ article: { code: c.code, designation: c.designation }, qty: c.qty })) : [{ article: null, qty: 1 }]);
  }, [open, kit]);

  const update = (i, patch) => setRows(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));

  const submit = async (e) => {
    e.preventDefault();
    if (rows.some((r) => !r.article)) return toast.error("Chaque ligne doit avoir un article.");
    setSaving(true);
    const body = { designation, components: rows.map((r) => ({ code: r.article.code, qty: Number(r.qty) })) };
    try {
      if (editing) await api.put(`/kits/${encodeURIComponent(kit.code)}`, body);
      else await api.post("/kits", { ...body, code });
      toast.success(editing ? "Kit mis à jour" : "Kit créé");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="kit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase">{editing ? `Modifier le kit ${kit.code}` : "Nouveau kit virtuel"}</DialogTitle>
          <DialogDescription>Un kit n'a pas de stock propre : il est composé uniquement d'articles simples.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Code</Label>
              <Input required disabled={editing} value={code} onChange={(e) => setCode(e.target.value)} className="h-12 font-mono" data-testid="kit-code-input" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Désignation</Label>
              <Input required value={designation} onChange={(e) => setDesignation(e.target.value)} className="h-12" data-testid="kit-designation-input" /></div>
          </div>
          <div className="space-y-3">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Composants</Label>
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`kit-component-row-${i}`}>
                <div className="flex-1"><ArticlePicker value={r.article} onChange={(a) => update(i, { article: a })} testid={`kit-component-picker-${i}`} /></div>
                <Input type="number" min="0.001" step="any" required value={r.qty} onChange={(e) => update(i, { qty: e.target.value })} className="h-12 w-24 font-mono" data-testid={`kit-component-qty-${i}`} />
                <Button type="button" variant="outline" className="h-12 w-12 p-0" disabled={rows.length === 1} onClick={() => setRows(rows.filter((_, k) => k !== i))} data-testid={`kit-component-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" className="h-11 gap-2" onClick={() => setRows([...rows, { article: null, qty: 1 }])} data-testid="kit-add-component-button"><Plus className="h-4 w-4" /> Ajouter un composant</Button>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="h-12" onClick={() => onOpenChange(false)} data-testid="kit-cancel-button">Annuler</Button>
            <Button type="submit" className="h-12 min-w-[140px]" disabled={saving} data-testid="kit-save-button">{saving ? "Enregistrement…" : "Enregistrer"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
